/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import invariant from 'invariant';
import React from 'react';
import { uniq, isEqual } from 'lodash';
import AlertDialog from 'components/AlertDialog.jsx';
import AnnouncementComposerRecipientsInput from './AnnouncementComposerRecipientsInput.jsx';
import ActivityComposerTooltipContainer from 'payments/containers/ActivityComposerTooltipContainer.jsx';
import Button from 'components/Button.jsx';
import ButtonGroup from 'components/ButtonGroup.jsx';
import CharacterCount from 'lib/CharacterCount';
import Composer from 'chat/components/Composer.jsx';
import ComposerAlert from 'chat/components/ComposerAlert.jsx';
import ComposerFilePreview from 'chat/components/ComposerFilePreview.jsx';
import CurrentUser from 'auth/CurrentUser';
import DatePickerInput from 'components/DatePickerInput.jsx';
import DropZoneModal from 'components/DropZoneModal.jsx';
import File from 'entities/File';
import FileUploaderMixin from 'components/mixins/FileUploaderMixin';
import FlexibleBlock from 'components/FlexibleBlock.jsx';
import Glyph from 'components/Glyph.jsx';
import GroupStore from 'groups/stores/GroupStore';
import LanguageHoverCard from 'chat/components/LanguageHoverCard.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import List from 'components/List.jsx';
import Message from 'entities/Message';
import MessageActions from 'groups/actions/MessageActions';
import { showPromptDialog } from 'components/promptDialog.jsx';
import SettingsStore from 'stores/SettingsStore';
import Text from 'components/Text.jsx';
import TimePickerInput from 'components/TimePickerInput.jsx';
import TranslationActions from 'actions/TranslationActions';
import UniversalComposer, { PANELS } from 'chat/components/UniversalComposer.jsx';
import { mountModal } from 'lib/modal.js';
import { PromptNames } from 'constants/PromptConstants';
import PromptStore from 'stores/PromptStore';
import PromptActions from 'actions/PromptActions';
import SafetyDialog from 'user/components/SafetyDialog.jsx';
import mountTymkPrompt from 'utils/mountTymkPrompt';

import styles from './styles/AnnouncementComposer.css';

import type { Target } from 'entities/Message';
import Leads from 'collections/Leads';
import { subgroupName } from 'utils/roleHelpers';

const ONE_MINUTE_IN_MS = 60 * 1000;
const FIVE_MINUTES_IN_MS = 5 * ONE_MINUTE_IN_MS;
const THIRTY_MINUTES_IN_MS = 30 * ONE_MINUTE_IN_MS;
const ONE_HOUR_IN_MS = 2 * THIRTY_MINUTES_IN_MS;

const getNext30MinInterval = (date = new Date()) => {
  const newDate = new Date(parseInt(date.getTime() / ONE_MINUTE_IN_MS, 10) * ONE_MINUTE_IN_MS);

  if (newDate.getMinutes() >= 30) {
    newDate.setMinutes(0);
    return new Date(newDate.getTime() + ONE_HOUR_IN_MS);
  }

  newDate.setMinutes(30);
  return newDate;
};

const getDefaultScheduledTime = () => {
  return new Date(getNext30MinInterval().getTime() + FIVE_MINUTES_IN_MS);
};

const getStateFromMessage = (message) => {
  const groups = message.getGroupIds().map(id => GroupStore.getGroup(id)).filter(o => !!o);

  return {
    groupCodes: groups.map(group => group.getCode()),
    scheduledTime: message.getSendAt(),
    text: message.getBody(),
    attachmentId: message.getAttachmentId(),
  };
};

type Props = {
  initialMessage: Message,
  autoFocus?: bool,
  onMessageSent: (targets: Target[]) => void,
  charLimit: number,
  charLimitWithFiles: number,
  groupCodes: string[],
  hideOldInput?: bool,
  inAnnouncementsThread?: bool,
  shouldShowModals?: bool,
  onRequestHide?: () => void,
  targets: Target[],
  isEdit?: bool,
  sendButtonText?: string,
  disableToField?: bool,
};

type State = {
  remainingCharactersCount: number,
  schedulerOpened: bool,
  translationOpened: bool,
  groupCodes: Array<*>,
  scheduledTime: ?Date,
  text: string,
  latestTranslation?: string,
  fileEntities?: Object, // FileUploaderMixin
  files?: any, // FileUploaderMixin
  fileData?: Object, // FileUploaderMixin
  isDraggingFile?: bool, // FileUploaderMixin
  isDraggingFileInner?: bool, // FileUploaderMixin
  dropzoneAttachFailed?: bool, // FileUploaderMixin
  uploadedFile?: ?File, // FileUploaderMixin
  leads?: Leads, // TYMKMixin
};

const AnnouncementComposer = React.createClass({
  propTypes: {
    initialMessage: React.PropTypes.instanceOf(Message).isRequired,
    autoFocus: React.PropTypes.bool,
    onMessageSent: React.PropTypes.func.isRequired,
    charLimit: React.PropTypes.number.isRequired,
    charLimitWithFiles: React.PropTypes.number.isRequired,
    groupCodes: React.PropTypes.arrayOf(React.PropTypes.string).isRequired,
    hideOldInput: React.PropTypes.bool,
    inAnnouncementsThread: React.PropTypes.bool,
    shouldShowModals: React.PropTypes.bool,
    onRequestHide: React.PropTypes.func,
    targets: React.PropTypes.array.isRequired,
    isEdit: React.PropTypes.bool,
    sendButtonText: React.PropTypes.string,
    disableToField: React.PropTypes.bool,
  },

  childContextTypes: {
    eventContextName: React.PropTypes.string,
  },

  mixins: [
    FileUploaderMixin,
  ],

  getDefaultProps() {
    return {
      initialMessage: new Message(),
      autoFocus: false,
      onMessageSent: () => {},
      shouldShowModals: true,
      groupCodes: [],
      hideOldInput: false,
      isEdit: false,
      disableToField: false,
    };
  },

  // $FlowIssue property props cannot be accessed on the global object
  getInitialState(props: Props = this.props): State {
    const { initialMessage } = props;
    const initialMessageProps = initialMessage ? getStateFromMessage(initialMessage) : {};

    return {
      remainingCharactersCount: this.getRemainingCharacters(initialMessageProps.text || ''),
      schedulerOpened: !!initialMessageProps.scheduledTime,
      translationOpened: false,
      groupCodes: uniq(this.props.groupCodes.concat(initialMessageProps.groupCodes)),
      scheduledTime: initialMessageProps.scheduledTime || null,
      text: initialMessageProps.text || '',
    };
  },

  getChildContext() {
    return {
      eventContextName: 'group_composer',
    };
  },

  componentDidMount() {
    // Called after the state is set so that the dropzone
    // is initialized (composer must expand)
    if (this.props.initialMessage) {
      for (const file of this.props.initialMessage.getFiles()) {
        this.addFile(file);
      }

      // Checks for a fileId passed in through query params
      const fileId = this.props.initialMessage.getAttachmentId();
      if (fileId) {
        this.getFileFromId(fileId, this.props.initialMessage);
      }
    }
  },

  componentWillReceiveProps(nextProps: Props) {
    // TODO remove group codes (we use targets now)
    const { groupCodes, initialMessage } = nextProps;
    if (this.props.groupCodes.length !== groupCodes.length) {
      this.setState({ groupCodes });
    }

    if (this.props.initialMessage !== initialMessage &&
      ((this.props.initialMessage && initialMessage) &&
      this.props.initialMessage.getBody() !== initialMessage.getBody() ||
      !isEqual(this.props.initialMessage.getTargets(), initialMessage.getTargets()))) {
      this.setState(this.getInitialState(nextProps));
    }
  },

  componentWillUnmount() {
    const message = this.createMessageFromState();
    const { targets, inAnnouncementsThread } = this.props;
    const group = targets[0] && GroupStore.getGroup(targets[0].group_id);
    const groupName = group ? group.getName() : '';
    if (!inAnnouncementsThread && message.isWorthSaving(groupName)) {
      MessageActions.saveDraftAnnouncement(message);
    } else if (!inAnnouncementsThread) {
      MessageActions.clearDraftAnnouncement();
    }
  },

  hasFiles() {
    return this.state && this.state.files && this.state.files.length > 0;
  },

  getRemainingCharacters(text: string) {
    const { charLimit, charLimitWithFiles } = this.props;
    return CharacterCount.getRemainingCharactersForText(
      charLimit,
      charLimitWithFiles,
      text,
      this.hasFiles());
  },

  onUploadFailed(file: File) {
    if (file.getSize() > this.getMaxFilesize()) {
      this.resetDropzone();
      mountModal(
        <AlertDialog
          eventScreenName="AnnouncementComposer"
          glyph="caution"
          glyphColor="lava"
          title={`File "${file.getName()}" is too large.`}
          subtext={`Files must be less than ${this.getMaxFilesizeName()}`}
        />,
        this
      );
    }
  },

  getDropzoneId() {
    if (this.props.inAnnouncementsThread) {
      return 'group_composer_thread';
    }
    return 'group_composer';
  },

  // Currently just used for prefilled attachments
  // typecheck: ignore statement
  getFileFromId(fileId: number, message: string) {
    const request = FileUploaderMixin.handleFileFromId(fileId, message);
    request.then((file) => {
      this.addFile(File.fromJSON(file));
    }).catch(e => console.error('AnnouncementComposer handleFileFromId failed', e));
  },

  resetComposerContent(groupCodes: string[] = []) {
    this.setState({
      remainingCharactersCount: this.getRemainingCharacters(''),
      schedulerOpened: false,
      translationOpened: false,
      groupCodes,
      scheduledTime: null,
      text: '',
    });
    this.clear();
  },

  offsetHeight: null,

  handleRecipientsChange(groupCodes: string[]) {
    this.setState({ groupCodes });
  },

  handleTextChange(event: SyntheticEvent) {
    // $FlowIssue
    const text = event.target.value;

    if (text === this.state.text) {
      return;
    }

    this.setState({
      text,
      remainingCharactersCount: this.getRemainingCharacters(text.trim()),
    });
  },

  submitComposer() {
    if (this.isSubmitDisabled()) {
      return;
    }

    if (!PromptStore.hasDismissed(PromptNames.SAFETY_MODAL) && ['student', 'parent'].includes(CurrentUser.getUser().getUserSelectedRole())) {
      const onSafetyDialogOkay = () => {
        PromptActions.recordDismissal(PromptNames.SAFETY_MODAL);
        this.sendMessage();
      };
      mountModal(<SafetyDialog onOkay={onSafetyDialogOkay} />, this);
      return;
    }

    this.sendMessage();
  },

  handleDatePickerChanged(newDate?: Date) {
    const { scheduledTime } = this.state;
    if (newDate) {
      // $FlowIssue not null at this point
      const oldDate = new Date(scheduledTime.getTime());
      newDate.setHours(oldDate.getHours());
      newDate.setMinutes(oldDate.getMinutes());
      this.setState({ scheduledTime: newDate });
    } else {
      this.setState({ scheduledTime });
    }
  },

  handleTimePickerChanged(inputDate: Date) {
    const { scheduledTime } = this.state;
    // $FlowIssue not null at this point
    const newDate = new Date(scheduledTime.getTime());
    newDate.setHours(inputDate.getHours());
    newDate.setMinutes(inputDate.getMinutes());
    this.setState({ scheduledTime: newDate });
  },

  isAttachEnabled() {
    return !this.state.translationOpened && !this.getFiles()[0];
  },

  isSubmitDisabled() {
    return this.state.remainingCharactersCount < 0 || !this.isValidMessage();
  },

  isValidMessage() {
    const isAddressed = (this.state.groupCodes.length ||
      (!!this.props.targets && this.props.targets.length));

    const fileData = this.getFileData(this.getFiles()[0]);
    const allUploadsComplete = fileData ? fileData.progress === 100 : true;

    const hasText = (this.state.text.trim().length > 0);

    return isAddressed && allUploadsComplete && (hasText || !!fileData);
  },

  createMessageFromState() {
    const { groupCodes } = this.state;
    const { targets } = this.props;

    const args = [
      targets ? null : groupCodes.map(code => GroupStore.getGroupForCode(code)).filter(g => !!g),
      this.state.text,
      this.state.scheduledTime,
      this.getFileEntities(), // FileUploaderMixin
      null,
      null,
      targets,
    ];

    return Message.createMessage(...args);
  },

  focus() {
    if (this.refs.composer) {
      this.refs.composer.focus();
    }
  },

  clear() {
    if (this.refs.composer) {
      this.refs.composer.clear();
    }
  },

  sendMessage() {
    if (this.props.isEdit) {
      this.editMessage();
      return;
    }
    const message = this.createMessageFromState();
    const { groupCodes, remainingCharactersCount } = this.state;

    // Dismiss the prompt showing the long announcements banner to this user
    const overLengthLimit = remainingCharactersCount < 0;
    const hasPrompt = CurrentUser.getUser().hasItemInPromptsInventory(PromptNames.LONG_ANNOUNCEMENTS_BANNER);
    if (overLengthLimit && hasPrompt) {
      const prompt = CurrentUser.getUser().findPrompt(PromptNames.LONG_ANNOUNCEMENTS_BANNER);
      PromptActions.recordDismissPromptsInventoryPrompt(prompt);
    }


    MessageActions.sendMessage(message)
      .then(() => {
        if (this.props.shouldShowModals) {
          this.showModals();
        }

        // TODO: WHY???
        GroupStore.invalidate();

        this.removeAllFiles();

        if (this.props.inAnnouncementsThread) {
          this.resetComposerContent(groupCodes);
        } else {
          this.setState({ text: '', groupCodes: [] });
        }

        this.props.onMessageSent(this.props.targets);
      }).catch(e => console.error('AnnouncementComposer sendMessage failed', e));
  },

  editMessage() {
    const body = this.state.text;
    const files = this.getFileEntities(); // FileUploaderMixin
    const sendAt = this.state.scheduledTime;
    invariant(sendAt, 'editMessage requires scheduledTime');
    MessageActions.updateScheduledMessage(
      this.props.initialMessage, body, this.props.targets, files, sendAt)
      .then(() => this.props.onMessageSent(this.props.targets)).catch(e => console.error('AnnouncementComposer updateScheduledMessage failed', e));
  },

  showSendToMoreClassesComposer() {
    const draftMessage = this.createMessageFromState();
    MessageActions.saveDraftAnnouncement(draftMessage);
    mountModal(
      <UniversalComposer
        canAnnounce
        panel={PANELS.CLASS_ANNOUCEMENT}
        onMessageSent={() => { mountTymkPrompt(this, 'send_announcement'); }}
      />, this);
  },

  // copied from GroupComposer, which this component has replaced
  showModals() {
    showPromptDialog(PromptNames.DESKTOP_NOTIFICATION_MODAL, 'sent_announcement', this);
  },

  toggleScheduler() {
    const scheduledTime = this.state.schedulerOpened ? null : getDefaultScheduledTime();

    this.setState({
      schedulerOpened: !this.state.schedulerOpened,
      scheduledTime,
    });
  },

  toggleTranslation() {
    this.setState(({ translationOpened }) => ({ translationOpened: !translationOpened }));
  },

  translateTo(lang: string) {
    TranslationActions
      .translate(this.state.text, lang)
      .then(response => {
        this.setState({
          text: response.translation,
          translationOpened: false,
          latestTranslation: lang,
          remainingCharactersCount: this.getRemainingCharacters(response.translation),
        });
      }).catch(e => console.error('AnnouncementComposer translate failed', e));
  },

  getPlaceholder() {
    const { targets } = this.props;
    const firstTarget = targets[0]; // In this component, there can be only a single target
    const groupName = firstTarget && firstTarget.name;
    const role = firstTarget && firstTarget.role;

    if (groupName && role) {
      return `Message ${subgroupName[role].toLowerCase()} in ${groupName}`;
    } else {
      return 'Type a message';
    }
  },

  renderComposer() {
    const { inAnnouncementsThread } = this.props;
    const { remainingCharactersCount } = this.state;
    const overLengthLimit = remainingCharactersCount < 0;

    let indicator;
    if (inAnnouncementsThread) {
      indicator = this.renderRemainingCharactersCount();
    }

    const messageOptions = (
      <div>
        {this.renderScheduler()}
        {this.renderTranslation()}
      </div>
    );

    return (
      <div className={styles.composerContainer}>
        <Composer
          ref="composer"
          className={
            cx(styles.composer, {
              [styles.announcementThreadComposer]: inAnnouncementsThread,
            })
          }
          placeholder={this.getPlaceholder()}
          autogrow
          value={this.state.text}
          autoFocus={this.props.autoFocus && this.state.groupCodes.length > 0}
          error={overLengthLimit}
          onChange={this.handleTextChange}
          indicator={indicator}>
          {this.renderActivityTooltip()}
          {this.renderFilePreview()}
          {messageOptions}
          {!inAnnouncementsThread && this.renderControls()}
        </Composer>
        {inAnnouncementsThread && this.renderControls()}
      </div>
    );
  },

  renderActivityTooltip() {
    const setting = SettingsStore.getComposerActivityRegex();
    const regex = new RegExp(setting, 'i');
    const user = CurrentUser.getUser();

    if (user && user.canCollectMoney() && this.state.text.match(regex)) {
      return <ActivityComposerTooltipContainer onActivitySent={this.props.onRequestHide} />;
    } else {
      return <span />;
    }
  },

  renderFilePreview() {
    const file = this.getFiles()[0];
    const fileData = this.getFileData(file);

    if (!fileData) {
      return null;
    }

    return (
      <div className={styles.filePreview}>
        <ComposerFilePreview
          progress={fileData.progress}
          thumbnail={fileData.thumbnail}
          type={fileData.type}
          error={fileData.error}
          onDelete={() => this.removeFile(file)}
          onRetry={() => this.retryUpload(file)}
          file={this.state.uploadedFile}
        />
      </div>
    );
  },

  renderControls() {
    const inUniversalComposer = !this.props.inAnnouncementsThread;
    const disabled = this.isSubmitDisabled();
    const hasMultipleGroups = GroupStore.getOwnedGroups().length > 1;
    const controlList = (
      <List className={styles.controls} horizontal>
        {this.renderAttachButton()}
        {this.renderScheduleButton()}
        {this.renderTranslationButton()}
        {!inUniversalComposer && hasMultipleGroups && !disabled && this.renderSendToMoreClassesButton()}
      </List>
    );
    return controlList;
  },

  renderAttachButton() {
    const tooltip = this.getFiles()[0] ? 'You can only attach 1 file' : null;
    const disabled = !this.isAttachEnabled();

    return (
      <div ref="dropzone_attach">
        <Button
          // We need for force this to be a link due to a Chrome bug, because
          // otherwise onmouseleave events won’t fire properly when this button
          // is disabled, so the tooltip will appear but won’t disappear when the
          // cursor leaves the button
          href={tooltip ? '#' : null}
          className={`addFileButton_${this.getDropzoneId()}`}
          onClick={this.focus}
          disabled={disabled}
          tooltip={tooltip}
          tooltipPlacement="top"
          level="text">
          <Glyph name="paperclip" className={styles.controlGlyph} />
          Attach
        </Button>
      </div>
    );
  },

  renderDropzoneModal() {
    if (this.state.isDraggingFile) {
      return (
        <DropZoneModal
          isFileUploaded={this.getFiles().length > 0}
          isHovering={this.state.isDraggingFileInner}
        />
      );
    } else {
      return null;
    }
  },

  renderSendToMoreClassesButton() {
    return (
      <li className={[styles.listItemPullRight, styles.moreClassesListItem].join(' ')}>
        <Button
          className={styles.moreClassesButton}
          level="text"
          onClick={this.showSendToMoreClassesComposer}>
          Add more classes
        </Button>
      </li>
    );
  },

  renderScheduleButton() {
    const disabled = !!this.state.scheduledTime || this.state.translationOpened;
    return (
      <Button
        disabled={disabled}
        level="text"
        onClick={this.toggleScheduler}>
        <Glyph name="schedule" className={styles.controlGlyph} />
        Schedule
      </Button>
    );
  },

  renderTranslationButton() {
    const disabled = this.state.translationOpened || this.state.text.length < 1;
    return (
      <Button
        level="text"
        disabled={disabled}
        eventElementName="show_translation_box"
        onClick={this.toggleTranslation}>
        <Glyph name="globe" size="medium" className={styles.controlGlyph} />
        Translate
      </Button>
    );
  },

  renderRecipientsInput() {
    return (
      <AnnouncementComposerRecipientsInput
        className={cx(styles.recipientsInput, {
          [styles.recipientsInputDisabled]: this.props.disableToField,
        })}
        selectedGroupCodes={this.state.groupCodes}
        // FIXME initially renders the layer relative to the window
        // autoFocus={this.props.autoFocus && this.state.groupCodes.length === 0}
        onChange={this.handleRecipientsChange}
      />
    );
  },

  renderScheduler() {
    if (!this.state.schedulerOpened) {
      return null;
    }

    const placement = (this.props.inAnnouncementsThread) ? 'top' : 'bottom';

    const datePicker = (
      <DatePickerInput
        value={this.state.scheduledTime}
        min={getNext30MinInterval()}
        onChange={this.handleDatePickerChanged}
        ref="timePicker"
        placement={placement}
      />
    );

    const timePicker = (
      <TimePickerInput
        className={cx({
          error: !!this.state.scheduledTime && new Date() > this.state.scheduledTime,
        })}
        value={this.state.scheduledTime}
        min={getNext30MinInterval()}
        onChange={this.handleTimePickerChanged}
        ref="timePicker"
        placement={placement}
      />
    );

    const closeButton = (
      <Button className={styles.schedulerCloseButton} level="text" onClick={this.toggleScheduler}>
        <Glyph name="close-circle" color="eclipse" size="large" solo />
      </Button>
    );

    return (
      <div className={styles.scheduler}>
        <Text className={styles.schedulerLabel} color="pitch" weight="semibold">
          Scheduled for:
        </Text>
        {datePicker} at {timePicker}
        {closeButton}
      </div>
    );
  },

  renderRemainingCharactersCount() {
    const { remainingCharactersCount } = this.state;

    const textColor = (remainingCharactersCount < 0) ? 'lava' : 'eclipse';
    return (
      <Text
        className={styles.remainingCharCount}
        color={textColor}
        size="xxsmall"
        data-test-selector="character_count">
        {remainingCharactersCount}
      </Text>
    );
  },

  renderSendButton() {
    const { remainingCharactersCount, latestTranslation, scheduledTime } = this.state;
    const disabled = this.isSubmitDisabled();

    let tooltip = null;
    if (disabled) {
      if (remainingCharactersCount < 0) {
        const charactersOverLimit = Math.abs(remainingCharactersCount);
        tooltip = (
          <div>
            {
              `This announcement is ${charactersOverLimit}
              character${charactersOverLimit === 1 ? '' : 's'} over the limit.
              Shorten your announcement by ${charactersOverLimit}
              character${charactersOverLimit === 1 ? '' : 's'} before
              ${scheduledTime ? 'scheduling' : 'sending'}.`
            }
          </div>
        );
      }
    }

    const eventProperties = {};

    if (latestTranslation) {
      eventProperties.language_code = latestTranslation;
    }

    return (
      <div className={styles.buttonContainer}>
        {!this.props.inAnnouncementsThread && this.renderRemainingCharactersCount()}
        <Button
          data-test-selector="announcement_composer_send_button"
          className={styles.sendButton}
          href={tooltip ? '#' : null} /* Note: Due to a Chrome bug, we force this to be a link */
          level="primary"
          size="small"
          eventElementName="send_button"
          eventProperties={eventProperties}
          tooltip={tooltip}
          tooltipPlacement="bottom"
          disabled={disabled}
          onClick={this.submitComposer}>
          {scheduledTime ? 'Save' : this.props.sendButtonText || 'Send'}
        </Button>
      </div>
    );
  },

  renderTranslation() {
    if (!this.state.translationOpened) {
      return null;
    }

    const languages = SettingsStore.getPrimaryLanguages();
    const buttons = [];

    // TODO: Implement two-pass render in ButtonGroup
    const displayedMax = Object.keys(languages).length - 1;
    let displayed = 0;

    for (const lang in languages) {
      displayed += 1;
      if (displayed <= displayedMax) {
        buttons.push(
          <Button
            eventElementName="translate_button"
            eventProperties={{
              language_code: lang,
            }}
            onClick={() => this.translateTo(lang)}>
            {languages[lang]}
          </Button>
        );
      }
    }

    buttons.push(
      <LayerTrigger
        layer={<LanguageHoverCard onLanguagePicked={lang => this.translateTo(lang)} />}
        trigger="click"
        placement="bottom"
        alignment="middle">
        <Button
          level="secondary"
          color="thunder"
          size="xsmall"
          eventElementName="show_more_languages_button">
          <Glyph name="ellipses" solo />
        </Button>
      </LayerTrigger>
    );

    return (
      <FlexibleBlock
        className={styles.translation}
        flex="left">
        <ButtonGroup
          className={styles.translationButtons}
          level="secondary"
          color="thunder"
          size="xsmall">
          {buttons}
        </ButtonGroup>
        <Button
          level="text"
          eventElementName="close_translation_box"
          onClick={this.toggleTranslation}>
          <Glyph name="close-circle" color="eclipse" size="large" solo />
        </Button>
      </FlexibleBlock>
    );
  },

  renderComposerAlert() {
    if (!this.props.inAnnouncementsThread) {
      return null;
    }

    const target = this.props.targets[0]; // always have only one if inAnnouncementsThread
    return (
      <ComposerAlert
        group={GroupStore.getGroup(target.group_id)}
        target={target}
      />
    );
  },

  renderComposerArea() {
    if (!CurrentUser.getUser().canComposeAnnouncements()) {
      return null;
    }

    return (
      <div className={styles.composerWrapper}>
        <div
          className={cx(styles.wrap, {
            [styles.dialogWrap]: !this.props.inAnnouncementsThread,
          })}
          key="composerAndButton">
          {this.renderComposer()}
          {this.renderSendButton()}
        </div>
        {this.renderDropzoneModal()}
      </div>
    );
  },

  render() {
    return (
      <div
        ref="dropzone"
        className={cx(styles.this, this.props.className, {
          [styles.announcementsThread]: this.props.inAnnouncementsThread,
        })}>
        {!this.props.hideOldInput && this.renderRecipientsInput()}
        {this.renderComposerAlert()}
        {this.renderComposerArea()}
      </div>
    );
  },
});

export default AnnouncementComposer;



// WEBPACK FOOTER //
// ./src/groups/components/AnnouncementComposer.jsx